// components/WhatWeDoSection.js
import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Container } from '../styles/StyledComponents';

// Section wrapper – consistent padding across devices
const SectionWrapper = styled.section`
  padding: 100px 2rem;
  background: #000000;

  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }

  @media (max-width: 480px) {
    padding: 60px 2rem;
  }
`;

// Title decoration
const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 3rem;
  width: 100%;
  overflow: hidden;
`;

const Square = styled(motion.div)`
  width: 15px;
  height: 15px;
  background: #ffffff;
  flex-shrink: 0;
`;

const Line = styled(motion.div)`
  flex: 1;
  height: 1px;
  background: #ffffff;
`;

const Title = styled(motion.h2)`
  font-size: 18px;
  color: #ffffff;
  margin: 0;
  white-space: nowrap;
  font-weight: 600;
  letter-spacing: 0.5px;
`;

// Main grid
const ContentWrapper = styled(motion.div)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

// Accordion styles
const AccordionContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const AccordionItem = styled(motion.div)`
  cursor: pointer;
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
  padding-bottom: 1rem;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const AccordionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;
`;

const AccordionTitle = styled(motion.h3)`
  color: #ffffff;
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  letter-spacing: 0.8px;
  transition: color 0.3s ease;

  ${AccordionItem}:hover & {
    color: #e2e8f0;
  }
`;

const PlusIcon = styled(motion.div)`
  color: #ffffff;
  font-size: 2rem;
  font-weight: 300;
  user-select: none;
`;

// Bullet-point content with custom styling
const AccordionContent = styled(motion.div)`
  overflow: hidden;
  color: #cbd5e1;
  font-size: 21px;
  line-height: 1.9;
  padding: 0 0 1.5rem 0;

  ul {
    margin: 1rem 0 0 0;
    padding: 0;
    list-style: none;
  }

  li {
    position: relative;
    padding-left: 1.6rem;
    margin-bottom: 0.7rem;

    &:before {
      content: "•";
      position: absolute;
      left: 0;
      color: #ffffff;
      font-weight: bold;
      font-size: 1.2em;
    }
  }
`;

// Image container
const ImageContainer = styled(motion.div)`
  width: 100%;
  height: 560px;
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);

  @media (max-width: 768px) {
    height: 400px;
  }
`;

const ServiceImage = styled(motion.img)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
`;

const WhatWeDoSection = () => {
  const [activeAccordion, setActiveAccordion] = useState(0);

  const services = [
    {
      title: 'Luxury & Fashion',
      bullets: [
        'C-Suite',
        'Buying and Merchandising',
        'Production and Product Development',
        'Wholesale, Sales, PR and Marketing',
        'Fashion Design',
        'Art Directors'
      ],
      image: '/lnf.jpg'
    },
    {
      title: 'Creative',
      bullets: [
        'C-suite',
        'Creative & Design',
        'Strategy',
        'Branding',
        'Integrated Pro',
        'Account & Project Management',
        'Support Services',
        'Human Resources',
        'Sales and Operations'
      ],
      image: '/CreativeStudio.jpg'
    },
    {
      title: 'Digital Industries',
      bullets: [
        'Digital Technologies',
        'C-suite',
        'Digital Marketing',
        'Digital Transformation',
        'Web Development',
        'UX/UI Design',
        'Design',
        'Product Management',
        'Community Management',
        'Customer Success',
        'AI Data Science'
      ],
      image: '/Digital_Tech.jpg'
    }
  ];

  // Animation variants
  const titleVariants = {
    hidden: { opacity: 0, x: -40 },
    visible: { opacity: 1, x: 0 }
  };

  const lineVariants = {
    hidden: { scaleX: 0 },
    visible: { scaleX: 1 }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.4
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
  };

  return (
    <SectionWrapper
      id="what-we-do"
      as={motion.section}
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, margin: "-150px" }}
      transition={{ duration: 1 }}
    >
      <Container>
        {/* Section Title */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <TitleContainer>
            <Square variants={titleVariants} transition={{ duration: 0.6 }} />
            <Title variants={titleVariants}>What We Do</Title>
            <Line
              variants={lineVariants}
              transition={{ duration: 1.2, ease: "easeInOut", delay: 0.5 }}
              style={{ originX: 0 }}
            />
          </TitleContainer>
        </motion.div>

        {/* Main Content */}
        <ContentWrapper
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          {/* Accordion List */}
          <AccordionContainer>
            {services.map((service, index) => (
              <AccordionItem
                key={index}
                onClick={() => setActiveAccordion(index)}
                variants={itemVariants}
                whileHover={{ x: 10 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <AccordionHeader>
                  <AccordionTitle
                    animate={{
                      color: activeAccordion === index ? "#ffffff" : "#b2b2b2"
                    }}
                    transition={{ duration: 0.3 }}
                  >
                    {service.title}
                  </AccordionTitle>
                  <PlusIcon
                    animate={{ rotate: activeAccordion === index ? 45 : 0 }}
                    transition={{ duration: 0.4, ease: "easeInOut" }}
                  >
                    +
                  </PlusIcon>
                </AccordionHeader>

                <AnimatePresence mode="wait">
                  {activeAccordion === index && (
                    <AccordionContent
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: "easeInOut" }}
                    >
                      <motion.ul
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2 }}
                      >
                        {service.bullets.map((bullet, i) => (
                          <motion.li
                            key={i}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.15 + i * 0.06 }}
                          >
                            {bullet}
                          </motion.li>
                        ))}
                      </motion.ul>
                    </AccordionContent>
                  )}
                </AnimatePresence>
              </AccordionItem>
            ))}
          </AccordionContainer>

          {/* Dynamic Image with Crossfade */}
          <ImageContainer>
            <AnimatePresence mode="wait">
              <ServiceImage
                key={activeAccordion}
                src={services[activeAccordion].image}
                alt={services[activeAccordion].title}
                initial={{ opacity: 0, scale: 1.08 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{
                  opacity: { duration: 0.6 },
                  scale: { duration: 0.8, ease: "easeOut" }
                }}
              />
            </AnimatePresence>
          </ImageContainer>
        </ContentWrapper>
      </Container>
    </SectionWrapper>
  );
};

export default WhatWeDoSection;